import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Add as AddIcon,
  Dashboard as DashboardIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { kanbanService, KanbanBoard } from '../services/kanbanService';
import { useKanbanStore } from '../store/kanbanStore';

const Kanban: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [newBoardDescription, setNewBoardDescription] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingBoard, setEditingBoard] = useState<KanbanBoard | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [boardMenuAnchor, setBoardMenuAnchor] = useState<null | HTMLElement>(null);
  const [boardMenuBoard, setBoardMenuBoard] = useState<KanbanBoard | null>(null);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [boardToDelete, setBoardToDelete] = useState<KanbanBoard | null>(null);
  const { setCurrentBoard } = useKanbanStore();

  // Buscar quadros
  const { data: boards, isLoading, error } = useQuery({
    queryKey: ['kanban-boards'],
    queryFn: () => kanbanService.getBoards(),
  });

  // Criar novo quadro
  const createBoardMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      kanbanService.createBoard(data),
    onSuccess: (newBoard) => {
      queryClient.invalidateQueries({ queryKey: ['kanban-boards'] });
      setOpenDialog(false);
      setNewBoardName('');
      setNewBoardDescription('');
      // Navegar para o novo quadro
      navigate(`/kanban/${newBoard.id}`);
    },
  });

  const handleCreateBoard = () => {
    if (!newBoardName.trim()) return;
    createBoardMutation.mutate({
      name: newBoardName,
      description: newBoardDescription || undefined,
    });
  };

  const handleOpenBoard = (boardId: string) => {
    navigate(`/kanban/${boardId}`);
  };

  // Editar quadro
  const updateBoardMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; description?: string } }) =>
      kanbanService.updateBoard(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-boards'] });
      setOpenEditDialog(false);
      setEditingBoard(null);
      setEditName('');
      setEditDescription('');
    },
  });

  const handleOpenEdit = (board: KanbanBoard) => {
    setBoardMenuAnchor(null);
    setBoardMenuBoard(null);
    setEditingBoard(board);
    setEditName(board.name);
    setEditDescription(board.description || '');
    setOpenEditDialog(true);
  };

  const handleSaveEdit = () => {
    if (!editingBoard || !editName.trim()) return;
    updateBoardMutation.mutate({
      id: editingBoard.id,
      data: { name: editName.trim(), description: editDescription.trim() || undefined },
    });
  };

  // Excluir quadro
  const deleteBoardMutation = useMutation({
    mutationFn: (id: string) => kanbanService.deleteBoard(id),
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: ['kanban-boards'] });
      setOpenDeleteConfirm(false);
      setBoardToDelete(null);
      // Se estiver na página do quadro excluído, volta para a lista
      if (window.location.pathname === `/kanban/${deletedId}`) {
        navigate('/kanban');
      }
    },
  });

  const handleOpenDeleteConfirm = (board: KanbanBoard) => {
    setBoardMenuAnchor(null);
    setBoardMenuBoard(null);
    setBoardToDelete(board);
    setOpenDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (boardToDelete) {
      deleteBoardMutation.mutate(boardToDelete.id);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" bgcolor="background.default">
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3} bgcolor="background.default">
        <Alert severity="error" onClose={() => {}}>
          Erro ao carregar quadros. Tente novamente.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: 'background.default', minHeight: '100vh', overflowX: 'hidden' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          p: { xs: 2, md: 2.5 },
          mb: { xs: 2, md: 3 },
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[1],
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.125rem' }, mb: 0.5 }}>
            Controle de Tarefas
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
            Gerencie seus quadros Kanban e organize suas tarefas
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="contained"
            fullWidth={isMobile}
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
            sx={{ color: '#fff', '&:hover': { color: '#fff' } }}
          >
            Novo Quadro
          </Button>
        </Box>
      </Box>

      {!boards || boards.length === 0 ? (
        <Box sx={{ borderRadius: 2, p: 4, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
          <Box textAlign="center" py={4}>
            <DashboardIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'text.primary', mb: 1 }} gutterBottom>
              Nenhum quadro encontrado
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Crie seu primeiro quadro para começar a organizar suas tarefas
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)} sx={{ color: '#fff', '&:hover': { color: '#fff' } }}>
              Criar Primeiro Quadro
            </Button>
          </Box>
        </Box>
      ) : (
        <>
        <Grid container spacing={{ xs: 2, md: 3 }}>
          {boards.map((board: KanbanBoard) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={board.id}>
              <Box
                sx={{
                  minHeight: 140,
                  p: 2,
                  borderRadius: 2,
                  position: 'relative',
                  cursor: 'pointer',
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: theme.shadows[1],
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  '&:hover': {
                    boxShadow: theme.shadows[4],
                    transform: 'translateY(-2px)',
                  },
                  ...(board.background_color && {
                    borderLeft: `4px solid ${board.background_color}`,
                  }),
                }}
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setBoardMenuBoard(board);
                    setBoardMenuAnchor(e.currentTarget);
                  }}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    color: 'text.secondary',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
                <Box onClick={() => handleOpenBoard(board.id)} sx={{ pr: 4 }}>
                  <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {board.name}
                  </Typography>
                  {board.description && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>{board.description}</Typography>
                  )}
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {new Date(board.last_activity_at).toLocaleDateString('pt-BR')}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Menu do quadro */}
        <Menu
          anchorEl={boardMenuAnchor}
          open={Boolean(boardMenuAnchor)}
          onClose={() => { setBoardMenuAnchor(null); setBoardMenuBoard(null); }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { minWidth: 160 } }}
        >
          <MenuItem
            onClick={() => boardMenuBoard && handleOpenEdit(boardMenuBoard)}
          >
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Editar nome</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => boardMenuBoard && handleOpenDeleteConfirm(boardMenuBoard)}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Excluir quadro</ListItemText>
          </MenuItem>
        </Menu>

        {/* Dialog editar quadro */}
        <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
          <DialogTitle sx={{ color: 'text.primary' }}>Editar Quadro</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Nome do Quadro"
              fullWidth
              variant="outlined"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Descrição (opcional)"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button onClick={() => setOpenEditDialog(false)} variant="outlined" color="inherit" sx={{ color: 'text.secondary' }}>
              Cancelar
            </Button>
            <Button
              onClick={handleSaveEdit}
              variant="contained"
              disabled={!editName.trim() || updateBoardMutation.isPending}
              sx={{ color: '#fff', '&:hover': { color: '#fff' } }}
            >
              {updateBoardMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmação excluir quadro */}
        <Dialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
          <DialogTitle sx={{ color: 'text.primary' }}>Excluir quadro?</DialogTitle>
          <DialogContent>
            <Typography color="text.secondary">
              Tem certeza que deseja excluir o quadro &quot;{boardToDelete?.name}&quot;? Esta ação não pode ser desfeita.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button onClick={() => setOpenDeleteConfirm(false)} variant="outlined" color="inherit" sx={{ color: 'text.secondary' }}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              disabled={deleteBoardMutation.isPending}
              sx={{ color: '#fff', '&:hover': { color: '#fff' } }}
            >
              {deleteBoardMutation.isPending ? 'Excluindo...' : 'Excluir'}
            </Button>
          </DialogActions>
        </Dialog>
        </>
      )}

      {/* Dialog para criar novo quadro */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            background: '#ffffff',
            border: '1px solid rgba(59, 130, 246, 0.15)',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(59, 130, 246, 0.06)',
          }
        }}
      >
        <DialogTitle sx={{ color: 'text.primary', fontWeight: 600 }}>
          Criar Novo Quadro
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome do Quadro"
            fullWidth
            variant="outlined"
            value={newBoardName}
            onChange={(e) => setNewBoardName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Descrição (opcional)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newBoardDescription}
            onChange={(e) => setNewBoardDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setOpenDialog(false)} variant="outlined" color="inherit" sx={{ color: 'text.secondary' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleCreateBoard}
            variant="contained"
            disabled={!newBoardName.trim() || createBoardMutation.isPending}
            sx={{ color: '#fff', '&:hover': { color: '#fff' } }}
          >
            {createBoardMutation.isPending ? 'Criando...' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Kanban;


